import { useMutation } from '@apollo/client/react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@optools/components/ui/alert-dialog';
import { Button } from '@optools/components/ui/button';
import { DELETE_SESSION_OVERRIDE } from '@optools/services/operator-gateway/overrides/session/mutations';
import { GET_SESSION_OVERRIDES } from '@optools/services/operator-gateway/overrides/session/queries';
import { Trash } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

export interface DeleteSessionOverrideProps {
  overrideId: string | undefined;
}

export function DeleteSessionOverride({ overrideId }: DeleteSessionOverrideProps) {
  const [open, setOpen] = useState(false);
  const [deleteSessionOverride, { loading }] = useMutation(DELETE_SESSION_OVERRIDE, {
    awaitRefetchQueries: true,
    onCompleted: async ({ deleteSessionOverride }) => {
      if (!deleteSessionOverride) {
        toast.error('Delete Override Error', {
          description: 'Failed to delete the override, please try again',
          duration: Infinity,
        });
      } else {
        toast.success('Session Override successfully deleted');

        setOpen(false);
      }
    },
    refetchQueries: [{ query: GET_SESSION_OVERRIDES }],
  });

  const handleOpenChange = (nextOpen: boolean) => {
    if (loading) return;
    setOpen(nextOpen);
  };

  const handleDelete = () => {
    if (!overrideId) return;

    deleteSessionOverride({ variables: { overrideId: +overrideId } });
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger asChild>
        <Button size="icon" title="Delete Override" variant="destructive">
          <Trash />
          <span className="sr-only">Delete Override</span>
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Override</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete this override?
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter className="mt-2">
          <AlertDialogCancel disabled={loading} variant="secondary">
            Cancel
          </AlertDialogCancel>
          <Button disabled={loading} loading={loading} variant="destructive" onClick={handleDelete}>
            Delete
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
